import { useCallback, useEffect, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import type {
  BjAction,
  BjBonusStatus,
  BjChatMessage,
  BjGameUpdate,
} from "@ziolo/shared";

const EMPTY_TABLE: BjGameUpdate = {
  dealerHand: [],
  phase: "waiting",
  dealerResult: null,
  roundResults: {},
  currentTurn: null,
  insuranceOpen: false,
  players: {},
  waitingList: [],
  dealerName: "Mietek",
  seatedCount: 0,
  bettingEndsAt: null,
  turnEndsAt: null,
};

export interface BlackjackSession extends BjBonusStatus {
  seated: boolean;
}

export function useBlackjackSocket(enabled: boolean) {
  const socketRef = useRef<Socket | null>(null);
  const [table, setTable] = useState<BjGameUpdate>(EMPTY_TABLE);
  const [session, setSession] = useState<BlackjackSession | null>(null);
  const [chat, setChat] = useState<BjChatMessage[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [walletEpoch, setWalletEpoch] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    // forceNew: Vite HMR otherwise reuses a dead Manager and the table sits on
    // "connecting" forever. polling first: websocket-first dies behind the Vite proxy.
    const socket = io(`${window.location.origin}/blackjack`, {
      path: "/socket.io/",
      withCredentials: true,
      transports: ["polling", "websocket"],
      forceNew: true,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      setError(null);
    });
    socket.on("disconnect", () => setConnected(false));
    socket.on("connect_error", (err: Error) => {
      setConnected(false);
      setError(
        err.message === "UNAUTHORIZED"
          ? "Sesja wygasła. Zaloguj się ponownie."
          : "Nie udało się połączyć ze stołem. Odśwież stronę.",
      );
    });
    socket.on("game_update", (update: BjGameUpdate) => setTable(update));
    socket.on("session", (next: BlackjackSession) => setSession(next));
    socket.on("chat:init", (payload: { messages: BjChatMessage[] }) => {
      setChat(payload.messages);
    });
    socket.on("chat:new", (message: BjChatMessage) => {
      setChat((prev) => [...prev, message].slice(-150));
    });
    socket.on("daily:status", (status: BjBonusStatus) => {
      setSession((prev) => ({ seated: prev?.seated ?? false, ...status }));
    });
    socket.on("daily:claimed", (result: { ok: boolean; chips?: number; nextAvailableAt?: number | null; reason?: string }) => {
      if (result.ok) {
        setWalletEpoch((n) => n + 1);
        setSession((prev) => ({
          seated: prev?.seated ?? false,
          canClaim: false,
          nextAvailableAt: result.nextAvailableAt ?? Date.now() + 2 * 60 * 60 * 1000,
          amount: 2137,
          chips: result.chips ?? prev?.chips ?? 0,
          keys: prev?.keys ?? 0,
          equippedSkin: prev?.equippedSkin ?? "classic",
        }));
      }
    });
    socket.on("notice", (payload: { text: string }) => {
      setNotice(payload.text);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [enabled]);

  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(id);
  }, [notice]);

  const emit = useCallback((event: string, payload?: unknown) => {
    const socket = socketRef.current;
    if (!socket) return;
    if (payload === undefined) socket.emit(event);
    else socket.emit(event, payload);
  }, []);

  return {
    table,
    session,
    chat,
    notice,
    connected,
    error,
    walletEpoch,
    join: (seatIndex?: number) => emit("join_game", seatIndex),
    leave: () => emit("leave_game"),
    placeBet: (amount: number) => emit("place_bet", amount),
    act: (action: BjAction) => emit("player_action", action),
    sendChat: (text: string) => emit("chat:send", { text, cid: crypto.randomUUID() }),
    claimBonus: () => emit("daily:claim"),
    refreshBonus: () => emit("daily:status"),
  };
}
